import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Textarea } from '@/components/ui/catalyst/textarea';
import {
  CreatePoliceDepartment,
  enhancedApi,
} from '@/services/api/generated/policeDepartmentsService';

import { US_STATES } from './utils';

export const PoliceDepartmentCreatePage = () => {
  const navigate = useNavigate();
  const [createDepartment, { isLoading }] =
    enhancedApi.usePoliceDepartmentsCreateMutation();

  const [formData, setFormData] = useState<{
    name: string;
    stateCode: string;
    stateName: string;
    description: string;
  }>({
    name: '',
    stateCode: '',
    stateName: '',
    description: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Department name is required';
    }

    if (!formData.stateCode.trim()) {
      newErrors.stateCode = 'State is required';
    } else if (formData.stateCode.trim().length !== 2) {
      newErrors.stateCode = 'State code must be 2 letters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const payload: CreatePoliceDepartment = {
        name: formData.name.trim(),
        state_code: formData.stateCode.trim().toUpperCase(),
        state_name: formData.stateName.trim() || undefined,
        description: formData.description.trim() || undefined,
        aliases: undefined,
        handles: undefined,
        jurisdiction_type: undefined,
        jurisdiction_name: undefined,
        enrichment_status: undefined,
        records_channels: undefined,
        time_zone: undefined,
        do_not_call: false,
      };

      const result = await createDepartment({
        createPoliceDepartment: payload,
      }).unwrap();

      toast.success('Department created successfully', {
        description:
          "We're gathering contact information and records request details from the internet. This usually takes a few moments.",
      });

      navigate(`/policedepartments/${result.id}`);
    } catch (error: any) {
      console.error('Failed to create department:', error);
      toast.error('Failed to create department', {
        description:
          error?.data?.error || 'Please check your information and try again.',
      });
    }
  };

  const handleStateChange = (value: string) => {
    setFormData({
      ...formData,
      stateCode: value,
      stateName:
        US_STATES.find((s) => s.code === value)?.name || formData.stateName,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-3xl px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Button
            plain
            onClick={() => navigate('/policedepartments')}
            className="mb-4"
          >
            <ArrowLeftIcon />
            Back to Departments
          </Button>

          <h1 className="text-3xl font-semibold text-gray-900">
            Create Police Department
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Add a new police department to the system. After creation,
            we&apos;ll automatically enrich it with contact details and records
            request information.
          </p>
        </div>

        {/* Info Banner */}
        <div className="mb-8 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex gap-3">
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900">
                Automatic Enrichment
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                Once you create the department, our system will automatically
                search the internet for contact methods, mailing addresses,
                online portals, and fee structures. This enrichment process
                typically completes within a few moments.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <form onSubmit={handleSubmit}>
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Department Name *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., San Francisco Police Department"
                    invalid={!!errors.name}
                    autoFocus
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </Field>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field>
                    <Label>State *</Label>
                    <Select
                      name="stateCode"
                      value={formData.stateCode}
                      onChange={(e) => handleStateChange(e.target.value)}
                      invalid={!!errors.stateCode}
                    >
                      <option value="">Select a state</option>
                      {US_STATES.map((state) => (
                        <option key={state.code} value={state.code}>
                          {state.name} ({state.code})
                        </option>
                      ))}
                    </Select>
                    {errors.stateCode && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.stateCode}
                      </p>
                    )}
                  </Field>

                  <Field>
                    <Label>State Name (Optional)</Label>
                    <Input
                      name="stateName"
                      value={formData.stateName}
                      onChange={(e) =>
                        setFormData({ ...formData, stateName: e.target.value })
                      }
                      placeholder="Auto-filled from state selection"
                    />
                  </Field>
                </div>

                <Field>
                  <Label>Description (Optional)</Label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Brief description of this police department..."
                    rows={3}
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Add any additional context about this department&apos;s
                    jurisdiction or services.
                  </p>
                </Field>
              </FieldGroup>
            </Fieldset>

            <div className="mt-8 flex items-center justify-end gap-3">
              <Button
                plain
                type="button"
                onClick={() => navigate('/policedepartments')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Creating...' : 'Create Department'}
              </Button>
            </div>
          </form>
        </div>

        {/* Help Text */}
        <div className="mt-6 rounded-lg bg-gray-50 p-4">
          <h3 className="text-sm font-medium text-gray-900">
            What happens next?
          </h3>
          <ul className="mt-2 space-y-1 text-sm text-gray-600">
            <li className="flex gap-2">
              <span className="text-gray-400">1.</span>
              <span>Department is created with basic information</span>
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">2.</span>
              <span>
                System searches for contact details, portals, and fee structures
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">3.</span>
              <span>
                Enriched data is automatically added to the department record
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-gray-400">4.</span>
              <span>
                You can manually edit or add additional information as needed
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
